// @flow
import React from 'react';
import type { ChildrenArray } from 'react';
import {
  usePropsValidation,
  types as t,
} from '../../validation/runtime-checks/index.js';
import { childrenArrayMatcher } from '../../childrenArrayMatcher.js';
import type { CompilerInjectedProps } from '../../validation/runtime-checks/types.js';
import { mathStringChecker } from '../../types.js';
import type { MathString } from '../../types.js';
import { sharedSubstantativeChildrenChecker } from '../common-children-types.js';
import type { SharedSubstantativeChildren } from '../common-children-types.js';
import { productColor, illustrationColor } from '../../designSystem.js';
import { InternalHeading } from '../../InternalHeading.js';
import {
  InternalSection,
  InternalPanel,
  InternalDescription,
} from '../common-components/internal-components.js';
import { INVTNContinueCondition } from './INVTNContinueCondition.js';
import {
  INVQuestionsPreamble,
  INVQuestion,
} from '../common-components/questions.js';

export function INVTNActivityGuide(props: {|
  title: string,
  children: ChildrenArray<
    | React$Element<typeof INVTNBackgroundSection>
    | React$Element<typeof INVTNActionsSection>
    | React$Element<typeof INVTNInvestigateSection>,
  >,
  _index: number,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, INVTNActivityGuide);
  if (failedValidation) return null;

  const { title, children, _index } = props;
  return (
    <div style={{ color: productColor.shuttleGrey, marginBottom: 40 }}>
      <InternalHeading level={2}>
        {`Activity ${_index + 1}: ${title}`}
      </InternalHeading>
      {children}
    </div>
  );
}
INVTNActivityGuide.spec = {
  title: t.string,
  children: childrenArrayMatcher(
    {
      min: 0,
      max: 1,
      elementType: t.elementType(INVTNBackgroundSection),
    },
    {
      min: 0,
      max: 1,
      elementType: t.elementType(INVTNActionsSection),
    },
    {
      min: 0,
      max: 1,
      elementType: t.elementType(INVTNInvestigateSection),
    },
  ),
};

export function INVTNBackgroundSection(props: {|
  timeEstimate?: number,
  children: ChildrenArray<
    | React$Element<typeof INVTNBackgroundDescription>
    | React$Element<typeof INVTNBackgroundPanel>
    | React$Element<typeof INVTNContinueCondition>,
  >,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    INVTNBackgroundSection,
  );
  if (failedValidation) return null;

  const { timeEstimate, children } = props;
  return (
    <InternalSection
      title={timeEstimate ? `Background (${timeEstimate} mins)` : 'Background'}
    >
      {children}
    </InternalSection>
  );
}
INVTNBackgroundSection.spec = {
  timeEstimate: t.optional(t.number),
  children: childrenArrayMatcher(
    {
      min: 1,
      max: 1,
      elementType: t.elementType(INVTNBackgroundDescription),
    },
    {
      min: 1,
      max: 1,
      elementType: t.elementType(INVTNBackgroundPanel),
    },
    {
      min: 0,
      max: 1,
      elementType: t.elementType(INVTNContinueCondition),
    },
  ),
};

export function INVTNActionsSection(props: {|
  timeEstimate?: number,
  children: ChildrenArray<
    | React$Element<typeof INVTNActionsDescription>
    | React$Element<typeof INVTNActionsPanel>
    | React$Element<typeof INVTNContinueCondition>,
  >,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, INVTNActionsSection);
  if (failedValidation) return null;

  const { timeEstimate, children } = props;
  return (
    <InternalSection
      title={timeEstimate ? `Actions (${timeEstimate} mins)` : 'Actions'}
    >
      {children}
    </InternalSection>
  );
}
INVTNActionsSection.spec = {
  timeEstimate: t.optional(t.number),
  children: childrenArrayMatcher(
    {
      min: 1,
      max: 1,
      elementType: t.elementType(INVTNActionsDescription),
    },
    {
      min: 1,
      max: 1,
      elementType: t.elementType(INVTNActionsPanel),
    },
    {
      min: 0,
      max: 1,
      elementType: t.elementType(INVTNContinueCondition),
    },
  ),
};

export function INVTNInvestigateSection(props: {|
  timeEstimate?: number,
  children: ChildrenArray<
    | React$Element<typeof INVTNInvestigateDescription>
    | React$Element<typeof INVTNAnswersPanel>
    | React$Element<typeof INVTNMisconceptionsPanel>
    | React$Element<typeof INVTNContinueCondition>,
  >,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    INVTNInvestigateSection,
  );
  if (failedValidation) return null;

  const { timeEstimate, children } = props;
  return (
    <InternalSection
      title={
        timeEstimate ? `Investigate (${timeEstimate} mins)` : 'Investigate'
      }
    >
      {children}
    </InternalSection>
  );
}
INVTNInvestigateSection.spec = {
  timeEstimate: t.optional(t.number),
  children: childrenArrayMatcher(
    {
      min: 1,
      max: 1,
      elementType: t.elementType(INVTNInvestigateDescription),
    },
    {
      min: 1,
      max: 1,
      elementType: t.elementType(INVTNAnswersPanel),
    },
    {
      min: 0,
      max: 1,
      elementType: t.elementType(INVTNMisconceptionsPanel),
    },
    {
      min: 0,
      max: 1,
      elementType: t.elementType(INVTNContinueCondition),
    },
  ),
};

export function INVTNBackgroundDescription(props: {|
  children: ChildrenArray<MathString>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    INVTNBackgroundDescription,
  );
  if (failedValidation) return null;

  return <InternalDescription>{props.children}</InternalDescription>;
}
INVTNBackgroundDescription.spec = {
  children: t.childrenArray(mathStringChecker),
};

export function INVTNBackgroundPanel(props: {|
  children: ChildrenArray<
    | React$Element<typeof INVQuestionsPreamble>
    | React$Element<typeof INVQuestion>,
  >,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, INVTNBackgroundPanel);
  if (failedValidation) return null;

  const children = React.Children.toArray(props.children);
  const [maybePreamble, ...rest] = children;
  const hasPreamble = maybePreamble.type.name === INVQuestionsPreamble.name;
  const questions = hasPreamble ? rest : children;
  return (
    <InternalPanel heading="Guiding Questions">
      {/* Flow has trouble matching this with the type given for InternalPanel children so it's
      wrapped in a fragment to make sure Flow knows it will get something workable */}
      <>
        {hasPreamble && maybePreamble}
        <ul style={{ margin: 0 }}>
          {questions.map((question, idx) => (
            <li key={idx}>{question}</li>
          ))}
        </ul>
      </>
    </InternalPanel>
  );
}
INVTNBackgroundPanel.spec = {
  children: childrenArrayMatcher(
    {
      min: 0,
      max: 1,
      elementType: t.elementType(INVQuestionsPreamble),
    },
    {
      min: 1,
      max: Infinity,
      elementType: t.elementType(INVQuestion),
    },
  ),
};

export function INVTNActionsDescription(props: {|
  children: ChildrenArray<MathString>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    INVTNActionsDescription,
  );
  if (failedValidation) return null;

  return <InternalDescription>{props.children}</InternalDescription>;
}
INVTNActionsDescription.spec = { children: t.childrenArray(mathStringChecker) };

export function INVTNActionsPanel(props: {|
  children: ChildrenArray<React$Element<typeof INVTNActionsSuggestedVariation>>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, INVTNActionsPanel);
  if (failedValidation) return null;

  return (
    <InternalPanel heading="Suggested Variations">
      {props.children}
    </InternalPanel>
  );
}
INVTNActionsPanel.spec = {
  children: t.childrenArray(t.elementType(INVTNActionsSuggestedVariation)),
};

export function INVTNActionsSuggestedVariation(props: {|
  children: ChildrenArray<
    | React$Element<typeof INVTNActionsSituation>
    | React$Element<typeof INVTNActionsVariation>,
  >,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    INVTNActionsSuggestedVariation,
  );
  if (failedValidation) return null;

  return <div>{props.children}</div>;
}
INVTNActionsSuggestedVariation.spec = {
  children: childrenArrayMatcher(
    {
      min: 1,
      max: 1,
      elementType: t.elementType(INVTNActionsSituation),
    },
    {
      min: 1,
      max: 1,
      elementType: t.elementType(INVTNActionsVariation),
    },
  ),
};

export function INVTNActionsSituation(props: {|
  children: ChildrenArray<MathString>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, INVTNActionsSituation);
  if (failedValidation) return null;

  return <div style={{ fontWeight: 600 }}>{props.children}:</div>;
}
INVTNActionsSituation.spec = {
  children: t.childrenArray(mathStringChecker),
};

export function INVTNActionsVariation(props: {|
  children: ChildrenArray<SharedSubstantativeChildren>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, INVTNActionsVariation);
  if (failedValidation) return null;

  return <div>{props.children}</div>;
}
INVTNActionsVariation.spec = {
  children: t.childrenArray(sharedSubstantativeChildrenChecker),
};

export function INVTNInvestigateDescription(props: {|
  children: ChildrenArray<MathString>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    INVTNInvestigateDescription,
  );
  if (failedValidation) return null;

  return <InternalDescription>{props.children}</InternalDescription>;
}
INVTNInvestigateDescription.spec = {
  children: t.childrenArray(mathStringChecker),
};

export function INVTNAnswersPanel(props: {|
  children: ChildrenArray<React$Element<typeof INVTNInvestigateAnswer>>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, INVTNAnswersPanel);
  if (failedValidation) return null;

  return (
    <InternalPanel heading="Answers">
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          paddingLeft: 16,
        }}
      >
        {React.Children.map(props.children, (child, idx) =>
          React.cloneElement(child, { _index: idx }),
        )}
      </div>
    </InternalPanel>
  );
}
INVTNAnswersPanel.spec = {
  children: t.childrenArray(t.elementType(INVTNInvestigateAnswer)),
};

export function INVTNInvestigateAnswer(props: {|
  children: ChildrenArray<SharedSubstantativeChildren>,
  _index: number,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    INVTNInvestigateAnswer,
  );
  if (failedValidation) return null;

  const { children, _index } = props;

  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <div
        style={{
          display: 'flex',
          flexShrink: 0,
          justifyContent: 'flex-end',
          width: 12,
          fontWeight: 600,
        }}
      >
        {_index + 1}.
      </div>
      <div>{children}</div>
    </div>
  );
}
INVTNInvestigateAnswer.spec = {
  children: t.childrenArray(sharedSubstantativeChildrenChecker),
};

export function INVTNMisconceptionsPanel(props: {|
  children: ChildrenArray<React$Element<typeof INVTNMisconceptionsDetail>>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    INVTNMisconceptionsPanel,
  );
  if (failedValidation) return null;

  return (
    <InternalPanel heading="Misconceptions">
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          paddingLeft: 10,
        }}
      >
        {props.children}
      </div>
    </InternalPanel>
  );
}
INVTNMisconceptionsPanel.spec = {
  children: t.childrenArray(t.elementType(INVTNMisconceptionsDetail)),
};

export function INVTNMisconceptionsDetail(props: {|
  children: ChildrenArray<
    | React$Element<typeof INVTNMisconceptionsItem>
    | React$Element<typeof INVTNPitfallItem>,
  >,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    INVTNMisconceptionsDetail,
  );
  if (failedValidation) return null;

  const children = React.Children.toArray(props.children);

  return <div>{children}</div>;
}
INVTNMisconceptionsDetail.spec = {
  children: childrenArrayMatcher(
    { min: 0, max: 1, elementType: t.elementType(INVTNMisconceptionsItem) },
    { min: 0, max: 1, elementType: t.elementType(INVTNPitfallItem) },
  ),
};

export function INVTNMisconceptionsItem(props: {|
  children: ChildrenArray<SharedSubstantativeChildren>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    INVTNMisconceptionsItem,
  );
  if (failedValidation) return null;

  return props.children;
}
INVTNMisconceptionsItem.spec = {
  children: t.childrenArray(sharedSubstantativeChildrenChecker),
};

export function INVTNPitfallItem(props: {|
  children: ChildrenArray<SharedSubstantativeChildren>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, INVTNPitfallItem);
  if (failedValidation) return null;

  return (
    <div
      style={{
        marginTop: 4,
        display: 'table',
        tableLayout: 'fixed',
        marginLeft: -16,
      }}
    >
      <div style={{ width: 16, display: 'table-cell' }}>
        <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M1.61065 9.38917C3.75854 11.5368 7.24104 11.5371 9.38917 9.38917C11.5371 7.24128 11.5368 3.75878 9.38917 1.61065C7.24128 -0.536764 3.75878 -0.537003 1.61065 1.61065C-0.537004 3.75878 -0.536765 7.24104 1.61065 9.38917ZM4.62376 2.60401C4.62376 2.11516 5.01997 1.71895 5.50882 1.71895C5.99766 1.71895 6.39387 2.11516 6.39387 2.60401L6.39387 6.14423C6.39387 6.63307 5.99766 7.02928 5.50882 7.02928C5.01997 7.02928 4.62376 6.63307 4.62376 6.14423L4.62376 2.60401ZM6.36359 8.46375C6.37421 8.01798 6.03435 7.65688 5.52456 7.65688C4.99353 7.65688 4.65396 8.01798 4.65367 8.46375C4.66458 8.93077 4.99382 9.28125 5.51423 9.28125C6.03435 9.28125 6.37421 8.93106 6.36359 8.46375Z"
            // this is not a product color but using this color was requested by design so
            fill={illustrationColor.tallPoppy}
          />
        </svg>
      </div>
      <div style={{ display: 'table-cell' }}>{props.children}</div>
    </div>
  );
}
INVTNPitfallItem.spec = {
  children: t.childrenArray(sharedSubstantativeChildrenChecker),
};
